import { createEventHook } from '@vueuse/core';
import { useForm } from 'vee-validate';
import { useToast } from 'vue-toastification';

import { type BasicImageMultiselectItem } from '@molecules/BasicImageMultiselect/BasicImageMultiselect.vue';
import { type BasicModalWizardStep } from '@molecules/BasicModalWizard/BasicModalWizard.vue';
import { CommonTranslationKeys } from '@utils/translationKeys';

import { type UserPreferencesValidationSchema } from '@organisms/UserPreferencesWizard.vue';
import { useAuthStore } from '@stores/auth';

import type {
    UserAttendanceFrequency,
    UserInterest,
    UserInterests,
} from '@repository/types';
import type { AnalyticsTrackFn } from '@plugins/04.analytics.client';

const PreferencesSurveyName = 'preferences';
export enum StepsCompleted {
    None = 0,
    Interests = 1,
    Frequency = 2,
}

export type UserPreferencesWizardStep = 1 | 2;

export type UserPreferencesSurveyField = 'interests' | 'frequency';

export const useUserPreferences = (
    startStep: UserPreferencesWizardStep,
    analyticsTrack: AnalyticsTrackFn,
    validationSchema: UserPreferencesValidationSchema
) => {
    const authStore = useAuthStore();
    const { $api } = useNuxtApp();

    const selectedFrequency = ref<UserAttendanceFrequency>(
        authStore.userPreferences?.attendanceFrequency
    );

    const currentStep = ref<UserPreferencesWizardStep>(startStep);
    const isFinishButtonLoading = ref(false);

    const isOpened = ref(false);
    setTimeout(() => (isOpened.value = true), 0);

    const closeHook = createEventHook();
    const completeStepHook = createEventHook<StepsCompleted>();

    const { success } = useToast();
    const { t } = useI18n();

    const getCurrentStepName = (step: UserPreferencesWizardStep) => {
        return step === 1 ? 'interests' : 'frequency';
    };

    const currentStepName = computed(() => {
        return getCurrentStepName(currentStep.value);
    });

    const translations = computed(() => ({
        interestsQuestion: t('atp.wizard.user_preferences.interests_question'),
        frequencyQuestion: t('atp.wizard.user_preferences.frequency_question'),
        interestsHeading: t('atp.wizard.user_preferences.interests_heading'),
        lastQuestion: t('atp.wizard.user_preferences.last_question'),
        finish: t('atp.wizard.user_preferences.finish'),
        skip: t('atp.wizard.user_preferences.skip'),
        savedSuccessfully: t(CommonTranslationKeys.SAVED_SUCSESSFULLY),
    }));

    const interestsTranslations = computed(() => ({
        finance: t('atp.wizard.user_preferences.finance'),
        education: t('atp.wizard.user_preferences.education'),
        parenting: t('atp.wizard.user_preferences.parenting'),
        art: t('atp.wizard.user_preferences.art'),
        technology: t('atp.wizard.user_preferences.new_technologies'),
        personalDevelopment: t(
            'atp.wizard.user_preferences.personal_development'
        ),
        medicine: t('atp.wizard.user_preferences.medicine'),
        marketing: t('atp.wizard.user_preferences.marketing'),
    }));

    const frequencyTranslations = computed(() => ({
        veryRarely: t('atp.wizard.user_preferences.very_rarely'),
        veryOften: t('atp.wizard.user_preferences.very_often'),
        sometimes: t('atp.wizard.user_preferences.sometimes'),
        rarely: t('atp.wizard.user_preferences.rarely'),
        often: t('atp.wizard.user_preferences.often'),
    }));

    const steps: BasicModalWizardStep[] = [
        { title: translations.value.interestsQuestion },
        { title: translations.value.lastQuestion },
    ];

    const frequencyOptions = [
        {
            text: frequencyTranslations.value.veryOften,
            value: 'very-often',
        },
        {
            text: frequencyTranslations.value.often,
            value: 'often',
        },
        {
            text: frequencyTranslations.value.sometimes,
            value: 'sometimes',
        },
        {
            text: frequencyTranslations.value.rarely,
            value: 'rarely',
        },
        {
            text: frequencyTranslations.value.veryRarely,
            value: 'very-rarely',
        },
    ];

    const initialValues = validationSchema.cast({
        interests: authStore.userPreferences?.interests || [],
    });

    const { errors, defineField } = useForm({
        validationSchema,
        initialValues,
        validateOnMount: !!initialValues.interests?.length,
    });

    const { translatedErrors } = useTranslatedErrors(errors);
    const [interests, interestsAttrs] = defineField('interests');

    const onFieldValueChanged = async (
        isSelected: boolean,
        field: UserPreferencesSurveyField,
        value: string
    ) => {
        const eventName = isSelected
            ? AmplitudeEvents.attendeeSurveyOptionSelected
            : AmplitudeEvents.attendeeSurveyOptionUnselected;
        await analyticsTrack(eventName, {
            value,
            field,
            name: PreferencesSurveyName,
            step: currentStepName.value,
        });
    };

    const onSelectedFrequencyChange = async (value: string) => {
        selectedFrequency.value = value as Exclude<
            UserAttendanceFrequency,
            undefined
        >;
        await onFieldValueChanged(true, 'frequency', value);
    };

    const getImageUrl = (name: UserInterest) => {
        return new URL(
            `../assets/images/user_preferences/${name}.jpg`,
            import.meta.url
        ).href;
    };

    const imageItems: BasicImageMultiselectItem[] = Object.keys(
        interestsTranslations.value
    ).map((key) => {
        return {
            imageSrc: getImageUrl(key as UserInterest),
            text: interestsTranslations.value[
                key as keyof typeof interestsTranslations.value
            ],
            value: key,
        };
    });

    const hasSelectedInterests = computed(() => {
        return interests.value.length > 0;
    });

    const hasSelectedFrequency = computed(() => {
        return !!selectedFrequency.value;
    });

    const handleNextStep = async () => {
        if (currentStep.value > 1) return;
        const step = getCurrentStepName(currentStep.value);
        currentStep.value++;

        const hasSaved = await savePreferences();
        setStoreUserPreferences(interests.value);
        await handleStepSaved(step);
        if (hasSaved) {
            await triggerStepCompletedHook(StepsCompleted.Interests);
        }
    };

    const handleStepSaved = async (step: PreferencesSurveyStep) => {
        await analyticsTrack(AmplitudeEvents.attendeeSurveyStepSaved, {
            name: PreferencesSurveyName,
            step,
        });
    };

    const handleSurveyFinished = async () => {
        await analyticsTrack(AmplitudeEvents.attendeeSurveyFinished, {
            name: PreferencesSurveyName,
        });
    };

    const setStoreUserPreferences = (
        interests: UserInterests,
        frequency?: UserAttendanceFrequency
    ) => {
        authStore.userPreferences = {
            interests,
            attendanceFrequency: frequency,
        };
    };

    const handleClose = async () => {
        await Promise.all([
            closeModal(),
            analyticsTrack(AmplitudeEvents.attendeeSurveyClosed, {
                name: PreferencesSurveyName,
                step: currentStepName.value,
            }),
        ]);
    };

    const closeModal = async () => {
        isOpened.value = false;
        await triggerCloseHook();
    };

    const savePreferences = async () => {
        try {
            await $api.user.savePreferences({
                interests: interests.value,
                attendanceFrequency: selectedFrequency.value,
            });
            return true;
        } catch (error) {
            return false;
        }
    };

    const triggerStepCompletedHook = async (step: StepsCompleted) => {
        await completeStepHook.trigger(step);
    };

    const triggerCloseHook = async () => {
        await closeHook.trigger();
    };

    const handleFinish = async () => {
        isFinishButtonLoading.value = true;
        const hasSaved = await savePreferences();

        if (hasSaved) {
            success(translations.value.savedSuccessfully);
            await triggerStepCompletedHook(StepsCompleted.Frequency);
        }

        setStoreUserPreferences(interests.value, selectedFrequency.value);
        isFinishButtonLoading.value = false;
        await closeModal();
        const step = getCurrentStepName(currentStep.value);
        await Promise.all([handleStepSaved(step), handleSurveyFinished()]);
    };

    onMounted(async () => {
        await analyticsTrack(AmplitudeEvents.attendeeSurveyViewed, {
            name: PreferencesSurveyName,
            step: currentStepName.value,
        });
    });

    return {
        handleNextStep,
        handleFinish,
        handleClose,
        errors,
        selectedFrequency,
        frequencyOptions,
        translatedErrors,
        interestsAttrs,
        translations,
        currentStep,
        imageItems,
        interests,
        isOpened,
        steps,
        hasSelectedInterests,
        hasSelectedFrequency,
        isFinishButtonLoading,
        onClose: closeHook.on,
        onCompleteStep: completeStepHook.on,
        onFieldValueChanged,
        onSelectedFrequencyChange,
    };
};
